<template>
  <div id="simple-calendar-app">
    <div class="vx-card app-fixed-height">
      <calendar-view
        ref="calendar"
        :displayPeriodUom="calendarView"
        :show-date="showDate"
        :events="simpleCalendarEvents"
        enableDragDrop
        :eventTop="windowWidth <= 400 ? '2rem' : '3rem'"
        eventBorderHeight="0px"
        eventContentHeight="1.65rem"
        class="theme-default"
        @click-date="openAddNewEvent"
        @click-event="openEditEvent"
        @drop-on-date="eventDragged">

        <div slot="header" class="mb-4">

          <div class="vx-row no-gutter">

            <!-- Month Name -->
            <div class="vx-col w-1/3 items-center sm:flex hidden">
              <!-- Add new event button -->
              <vs-button icon-pack="feather" icon="icon-plus" @click="promptAddNewEvent(new Date())">Add</vs-button>
            </div>

            <!-- Current Month -->
            <div class="vx-col sm:w-1/3 w-full sm:my-0 my-3 flex justify-center order-last">
              <div class="flex items-center">
                <feather-icon icon="ChevronLeftIcon" @click="updateMonth(-1)" class="cursor-pointer bg-primary text-white rounded-full" />
                <span class="mx-3 text-xl font-medium whitespace-no-wrap">{{ showDate | month }}</span>
                <feather-icon icon="ChevronRightIcon" @click="updateMonth(1)" class="cursor-pointer bg-primary text-white rounded-full" />
              </div>
            </div>

            <div class="vx-col sm:w-1/3 w-full flex justify-center">
              <vs-button
                class="rounded-r-none p-3 md:px-8 md:py-3"
                @click="calendarView = 'month'">Month</vs-button>
              <vs-button
                class="rounded-r-none rounded-l-none p-3 md:px-8 md:py-3"
                @click="calendarView = 'week'">Week</vs-button>
              <vs-button
                class="rounded-l-none p-3 md:px-8 md:py-3"
                @click="calendarView = 'year'">Year</vs-button>
            </div>
          </div>

        </div>
      </calendar-view>
    </div>

    <!-- Assign SOP Modal -->
    <AssignSOPModal
      ref="assignSOPModal"
      title="Assign SOP"
      :description="assignSOPDescription"
      :prefilled-date="prefilledDate"
      @assignment-created="onAssignmentCreated" />

    <!-- EDIT EVENT -->
    <vs-prompt
        class="calendar-event-dialog"
        vs-title="Edit Event"
        vs-accept-text= "Submit"
        vs-cancel-text = "Remove"
        vs-button-cancel = "border"
        @vs-cancel="removeEvent"
        @vs-accept="editEvent"
        :vs-is-valid="validForm"
        :vs-active.sync="activePromptEditEvent">

        <div class="calendar__label-container flex">

            <vs-chip v-if="labelLocal != 'none'" class="text-white" :class="'bg-' + labelColor(labelLocal)">{{ labelLocal }}</vs-chip>

            <vs-dropdown vs-custom-content class="ml-auto my-2 cursor-pointer">

                <feather-icon icon="TagIcon" svgClasses="h-5 w-5" @click.prevent></feather-icon>

                <vs-dropdown-menu style="z-index: 200001">
                        <vs-dropdown-item v-for="(label, index) in calendarLabels" :key="index" @click="labelLocal = label.value">
                            <div class="h-3 w-3 inline-block rounded-full mr-2" :class="'bg-' + label.color"></div>
                            <span>{{ label.text }}</span>
                        </vs-dropdown-item>

                        <vs-dropdown-item @click="labelLocal = 'none'">
                            <div class="h-3 w-3 mr-1 inline-block rounded-full mr-2 bg-primary"></div>
                            <span>None</span>
                        </vs-dropdown-item>
                </vs-dropdown-menu>
            </vs-dropdown>

        </div>

        <vs-input name="event-name" v-validate="'required'" class="w-full" label-placeholder="Event Title" v-model="title"></vs-input>
        <div class="my-4">
            <small class="date-label">Start Date</small>
            <datepicker :disabledDates="disabledDatesFrom" name="start-date" v-model="startDate"></datepicker>
        </div>
        <div class="my-4">
            <small class="date-label">End Date</small>
            <datepicker :disabledDates="disabledDatesTo" name="end-date" v-model="endDate"></datepicker>
        </div>
        <vs-input name="event-url" v-validate="'url'" class="w-full mt-6" label-placeholder="Event URL" v-model="url" :color="!errors.has('event-url') ? 'success' : 'danger'"></vs-input>

    </vs-prompt>

    <!-- REVIEW DETAILS MODAL -->
    <vs-popup
        :title="getModalTitle()"
        :active.sync="activeReviewDetailsModal"
        class="review-details-dialog">
        
        <div v-if="selectedReviewEvent" class="modern-assignment-details">
            <!-- Clean Title -->
            <div class="assignment-header">
                <h3 class="assignment-title">{{ selectedReviewEvent.customTitle || selectedReviewEvent.onboardingName }}</h3>
                <p class="assignment-description" v-if="selectedReviewEvent.onboardingDescription">
                    {{ selectedReviewEvent.onboardingDescription }}
                </p>
            </div>

            <!-- Due Date with Calendar Icon -->
            <div class="due-date-section">
                <div class="due-date-container">
                    <i class="fas fa-calendar-alt due-date-icon"></i>
                    <div class="due-date-info">
                        <span class="due-date-label">Due Date</span>
                        <span class="due-date-value">{{ formatDate(selectedReviewEvent.startDate) }}</span>
                    </div>
                </div>
                <div class="status-badge" :class="getStatusClass(selectedReviewEvent)">
                    {{ getStatusText(selectedReviewEvent) }}
                </div>
            </div>

            <!-- Completion Status -->
            <div v-if="selectedReviewEvent.completionStatus" class="completion-section">
                <div class="completion-info">
                    <i class="fas fa-check-circle text-success mr-2"></i>
                    <span class="completion-label">Completed by</span>
                    <span class="completion-user">{{ selectedReviewEvent.completionStatus.completedBy }}</span>
                    <span class="completion-date">on {{ formatDate(selectedReviewEvent.completionStatus.completedAt) }}</span>
                </div>
            </div>

            <!-- Assignee Info -->
            <div class="assignee-section" v-if="selectedReviewEvent.reviewerEmail">
                <div class="assignee-info">
                    <span class="assignee-label">Assigned to</span>
                    <span class="assignee-email">{{ selectedReviewEvent.reviewerEmail }}</span>
                    <span v-if="selectedReviewEvent.reviewerName" class="assignee-name">
                        ({{ selectedReviewEvent.reviewerName }})
                    </span>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-between mt-6">
                <vs-button 
                    color="danger" 
                    type="border" 
                    @click="showDeleteConfirmation"
                    :disabled="deletingAssignment">
                    <vs-loading v-if="deletingAssignment" />
                    {{ deletingAssignment ? 'Deleting...' : 'Delete Assignment' }}
                </vs-button>
                
                <div class="flex gap-2">
                    <vs-button 
                        v-if="selectedReviewEvent.processRunId && selectedReviewEvent.onboardingId"
                        color="success" 
                        type="border" 
                        @click="refreshCurrentAssignmentStatus"
                        :disabled="refreshingStatus">
                        <vs-loading v-if="refreshingStatus" />
                        <i v-else class="fas fa-sync-alt mr-1"></i>
                        {{ refreshingStatus ? 'Refreshing...' : 'Refresh Status' }}
                    </vs-button>
                    
                    <vs-button 
                        color="primary" 
                        type="border" 
                        @click="closeReviewDetails">
                        Close
                    </vs-button>
                    <vs-button 
                        color="success" 
                        @click="openSOP"
                        class="ml-2">
                        Open SOP
                    </vs-button>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Dialog -->
        <vs-popup
            title="Confirm Deletion"
            :active.sync="showDeleteConfirm"
            class="delete-confirmation-dialog">
            
            <div class="text-center">
                <div class="mb-4">
                    <i class="fas fa-exclamation-triangle text-danger text-4xl mb-3"></i>
                    <h3 class="text-danger mb-2">Delete Assignment</h3>
                    <p class="text-gray-600">
                        Are you sure you want to delete this {{ (selectedReviewEvent && selectedReviewEvent.isCompleteSOP) ? 'SOP completion' : 'review' }} assignment?
                    </p>
                    <p class="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
                </div>
                
                <div class="flex justify-center space-x-3">
                    <vs-button 
                        color="danger" 
                        @click="confirmDeleteAssignment"
                        :disabled="deletingAssignment">
                        <vs-loading v-if="deletingAssignment" />
                        {{ deletingAssignment ? 'Deleting...' : 'Yes, Delete' }}
                    </vs-button>
                    <vs-button 
                        color="primary" 
                        type="border" 
                        @click="showDeleteConfirm = false">
                        Cancel
                    </vs-button>
                </div>
            </div>
        </vs-popup>
    </vs-popup>
  </div>
</template>

<script>
import { CalendarView, CalendarViewHeader } from "vue-simple-calendar"
require("vue-simple-calendar/static/css/default.css")

import Datepicker from 'vuejs-datepicker';
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';
import AssignSOPModal from '@/components/AssignSOPModal.vue';

export default {
  components: {
    CalendarView,
    CalendarViewHeader,
    Datepicker,
    AssignSOPModal
  },
  data() {
    return {
      showDate: new Date(),
      calendarView: 'month',

      activePromptEditEvent: false,
      activeReviewDetailsModal: false,
      selectedReviewEvent: null,
      deletingAssignment: false,
      showDeleteConfirm: false,
      refreshingStatus: false,
      refreshingAllStatus: false,
      prefilledDate: null,
      assignSOPDescription: 'Assign an SOP to a user with a due date.',
      
      // Event editing properties
      id: null,
      title: '',
      startDate: null,
      endDate: null,
      url: '',
      labelLocal: 'none',
    }
  },
  computed: {
    simpleCalendarEvents() {
        // Combine calendar events with todos that have due dates and review assignments
        const calendarEvents = this.$store.state.calendar.simpleCalendarEvents;
        const todosWithDueDates = this.$store.state.todo.todoArray
            .filter(todo => todo.dueDate && !todo.isTrashed)
            .map(todo => ({
                id: `todo-${todo.id}`,
                title: `📋 ${todo.title}`,
                startDate: new Date(todo.dueDate),
                endDate: new Date(todo.dueDate),
                url: '',
                classes: this.getTodoEventClass(todo),
                label: 'todo',
                todoId: todo.id,
                isTodo: true,
                isDone: todo.isDone,
                isOverdue: this.isTodoOverdue(todo)
            }));
        
        const reviewAssignments = this.$store.state.calendar.reviewAssignments || [];
        const reviewEvents = reviewAssignments
            .filter(review => review.dueForReview)
            .map(review => {
                const isCompleteSOP = review.type === 'COMPLETE_SOP';
                const eventType = isCompleteSOP ? 'complete-sop' : 'review';
                const titlePrefix = isCompleteSOP ? '' : '';
                
                // Use custom title if set, otherwise use SOP name
                const eventTitle = review.customTitle || review.onboarding?.name || 'Unknown Onboarding';
                
                return {
                    id: `${eventType}-${review.id}`,
                    title: `${titlePrefix} ${eventTitle}`,
                    startDate: new Date(review.dueForReview),
                    endDate: new Date(review.dueForReview),
                    url: '',
                    classes: this.getReviewEventClass(review),
                    label: eventType,
                    reviewId: review.id,
                    isReview: !isCompleteSOP,
                    isCompleteSOP: isCompleteSOP,
                    isOverdue: this.isReviewOverdue(review),
                    // Additional data for review details
                    onboardingId: review.onboardingId,
                    onboardingName: review.onboarding?.name || 'Unknown Onboarding',
                    onboardingDescription: review.onboarding?.description || '',
                    reviewerEmail: review.user?.email || '',
                    reviewerName: review.user?.firstName && review.user?.lastName 
                        ? `${review.user.firstName} ${review.user.lastName}` 
                        : review.user?.firstName || review.user?.lastName || '',
                    assignmentType: review.type,
                    customTitle: review.customTitle
                };
            });
        
        return [...calendarEvents, ...todosWithDueDates, ...reviewEvents];
    },
    calendarLabels() {
        return [
            ...this.$store.state.calendar.calendarLabels,
            { text: 'Review', value: 'review', color: 'info' },
            { text: 'Complete SOP', value: 'complete-sop', color: 'success' }
        ]
    },
    labelColor() {
        return (label) => {
            if (label == "business") return "success"
            else if (label == "work") return "warning"
            if (label == "personal") return "danger"
            if (label == "todo") return "primary"
            if (label == "review") return "info"
            if (label == "complete-sop") return "success"
            if (label == "none") return "primary"
        }
    },
    windowWidth() {
      return this.$store.state.windowWidth
    }
  },
  mounted() {
    this.loadReviewAssignments();
    
    // Listen for focus events to refresh completion status
    window.addEventListener('focus', this.handleWindowFocus);
  },
  
  beforeDestroy() {
    window.removeEventListener('focus', this.handleWindowFocus);
  },
  methods: {
    addEvent() {
        // This method is no longer used - replaced by AssignSOPModal
        console.log('addEvent method is deprecated - use AssignSOPModal instead');
    },
    updateMonth(val) {
      this.showDate = this.$refs.calendar.getIncrementedPeriod(val);
    },
    promptAddNewEvent(date) {
        this.openAssignSOPModal(date);
    },
    addNewEventDialog(date) {
        // Deprecated - use openAssignSOPModal instead
        this.openAssignSOPModal(date);
    },
    openAddNewEvent(date) {
        this.openAssignSOPModal(date);
    },
    openAssignSOPModal(date) {
        // Format date for the modal
        if (date instanceof Date) {
          this.prefilledDate = date.toISOString().split('T')[0];
        } else {
          this.prefilledDate = null;
        }
        
        // Open the Assign SOP modal
        this.$refs.assignSOPModal.open();
    },
    onAssignmentCreated(assignmentData) {
        console.log('Assignment created:', assignmentData);
        
        // Refresh the calendar to show the new assignment
        this.loadReviewAssignments();
        
        this.$vs.notify({
            title: 'Success',
            text: `SOP "${assignmentData.sop.name}" assigned successfully`,
            color: 'success'
        });
    },
    openEditEvent(event) {
      console.log('openEditEvent called with:', event);
      console.log('Event properties:', {
        id: event?.id,
        isTodo: event?.isTodo,
        isReview: event?.isReview,
        title: event?.title
      });
      
      // Check if event exists and has an id
      if (!event || !event.id) {
        console.error('Invalid event object:', event);
        return;
      }

      // Check if it's a review event (by ID prefix or isReview property)
      if (event.isReview || (event.id && event.id.startsWith('review-'))) {
        console.log('Handling review event');
        // Show review details modal
        this.showReviewDetails(event);
        return;
      }
      
      // Check if it's a CompleteSOP event (by ID prefix or isCompleteSOP property)
      if (event.isCompleteSOP || (event.id && event.id.startsWith('complete-sop-'))) {
        console.log('Handling CompleteSOP event');
        // Show review details modal (same modal, different content)
        this.showReviewDetails(event);
        return;
      }
      
      // Check if it's a todo event (by ID prefix or isTodo property)
      if (event.isTodo || (event.id && event.id.startsWith('todo-'))) {
        console.log('Handling todo event');
        // Navigate to todo app or show todo details
        this.$router.push('/pages/todo');
        return;
      }
      
      // For regular calendar events, use the store getter
      const e = this.$store.getters['calendar/simpleCalendareventById'](event.id);
      if (!e) {
        console.error('Event not found in store:', event.id);
        return;
      }
      
      this.id = e.id;
      this.title = e.title;
      this.startDate = e.startDate;
      this.endDate = e.endDate;
      this.url = e.url;
      this.labelLocal = e.label;
      this.activePromptEditEvent = true;
    },
    editEvent() {
      const obj = { id: this.id, title: this.title, startDate: this.startDate, endDate: this.endDate, label: this.labelLocal, url: this.url }
      obj.classes = "event-" + this.labelColor(this.labelLocal)
      this.$store.dispatch('calendar/editSimpleCalendarEvent', obj)
    },
    removeEvent() {
      this.$store.dispatch('calendar/removeSimpleCalendarEvent', this.id)
    },
    eventDragged(event, date) {
      this.$store.dispatch('calendar/simpleCalendarEventDragged', {event: event, date: date})
    },
    getTodoEventClass(todo) {
      if (todo.isDone) {
        return 'event-success'; // Completed todos - green
      } else if (this.isTodoOverdue(todo)) {
        return 'event-danger'; // Overdue todos - red
      } else {
        return 'event-warning'; // Pending todos - yellow/orange
      }
    },
    isTodoOverdue(todo) {
      if (!todo.dueDate || todo.isDone) return false;
      const today = new Date();
      const due = new Date(todo.dueDate);
      return due < today;
    },
    getReviewEventClass(review) {
      if (this.isReviewOverdue(review)) {
        return 'event-danger'; // Overdue reviews - red
      } else {
        return 'event-success'; // Pending reviews - green
      }
    },
    isReviewOverdue(review) {
      if (!review.dueForReview) return false;
      const today = new Date();
      const due = new Date(review.dueForReview);
      return due < today;
    },
    async loadReviewAssignments() {
      try {
        const query = `
          query {
            reviewAssignmentsForCalendar {
              id
              userId
              onboardingId
              dueForReview
              cadence
              type
              customTitle
              user {
                email
                firstName
                lastName
              }
              onboarding {
                id
                name
                description
              }
            }
          }
        `;

        const req = {
          query: query
        };

        const response = await TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        });

        if (response && response.data && response.data.data && response.data.data.reviewAssignmentsForCalendar) {
          this.$store.dispatch('calendar/loadReviewAssignments', response.data.data.reviewAssignmentsForCalendar);
        }
      } catch (error) {
        console.error('Error loading review assignments:', error);
      }
    },
    async showReviewDetails(event) {
      console.log('showReviewDetails called with:', event);
      
      // If the event doesn't have review properties, try to reconstruct them
      if (!event.onboardingId && event.id && (event.id.startsWith('review-') || event.id.startsWith('complete-sop-'))) {
        const reviewId = event.id.replace('review-', '').replace('complete-sop-', '');
        const reviewAssignments = this.$store.state.calendar.reviewAssignments || [];
        const reviewData = reviewAssignments.find(r => r.id == reviewId);
        
        if (reviewData) {
          console.log('Found review data:', reviewData);
          // Reconstruct the event with review data
          event.onboardingId = reviewData.onboardingId;
          event.onboardingName = reviewData.onboarding?.name || 'Unknown Onboarding';
          event.onboardingDescription = reviewData.onboarding?.description || '';
          event.reviewerEmail = reviewData.user?.email || '';
          event.reviewerName = reviewData.user?.firstName && reviewData.user?.lastName 
            ? `${reviewData.user.firstName} ${reviewData.user.lastName}` 
            : reviewData.user?.firstName || reviewData.user?.lastName || '';
          event.customTitle = reviewData.customTitle;
          event.processRunId = reviewData.processRunId;
        }
      }
      
      // Check for completion status if this is a ProcessRun assignment
      if (event.processRunId && event.onboardingId) {
        event.completionStatus = await this.checkCompletionStatus(event.processRunId, event.onboardingId);
      }
      
      this.selectedReviewEvent = event;
      this.activeReviewDetailsModal = true;
    },
    closeReviewDetails() {
      this.activeReviewDetailsModal = false;
      this.selectedReviewEvent = null;
    },
    openSOP() {
      if (this.selectedReviewEvent && this.selectedReviewEvent.onboardingId) {
        const sopUrl = `/pages/pdf-view?oid=${this.selectedReviewEvent.onboardingId}&mode=slideshow`;
        this.$router.push(sopUrl);
        this.closeReviewDetails();
      }
    },

    // Method to refresh completion status for a specific assignment
    async refreshAssignmentCompletionStatus(processRunId, onboardingId) {
      try {
        console.log(`Refreshing completion status for ProcessRun ${processRunId}, Onboarding ${onboardingId}`);
        const completionStatus = await this.checkCompletionStatus(processRunId, onboardingId);
        console.log('Completion status result:', completionStatus);
        
        // Update the event in the calendar events
        const events = this.simpleCalendarEvents;
        if (!events || !Array.isArray(events)) {
          console.log('No calendar events available for update');
          return completionStatus;
        }
        
        const eventIndex = events.findIndex(event => 
          event.processRunId === processRunId && event.onboardingId === onboardingId
        );
        
        console.log(`Found event at index: ${eventIndex}`);
        
        if (eventIndex !== -1) {
          events[eventIndex].completionStatus = completionStatus;
          console.log(`Updated event completion status:`, events[eventIndex]);
          // Force reactivity update
          this.$forceUpdate();
        }
        
        return completionStatus;
      } catch (error) {
        console.error('Error refreshing completion status:', error);
        return null;
      }
    },

    // Handle window focus to refresh completion status
    async handleWindowFocus() {
      console.log('Window focused - checking for completion status updates');
      // Small delay to ensure the window is fully focused
      setTimeout(async () => {
        try {
          // Refresh completion status for all ProcessRun assignments
          const events = this.simpleCalendarEvents;
          if (!events || !Array.isArray(events)) {
            console.log('No calendar events available');
            return;
          }
          
          const processRunEvents = events.filter(event => 
            event.processRunId && event.onboardingId
          );
          
          console.log(`Found ${processRunEvents.length} ProcessRun events to check`);
          
          for (const event of processRunEvents) {
            console.log(`Checking completion status for event: ${event.title}, ProcessRun: ${event.processRunId}, Onboarding: ${event.onboardingId}`);
            await this.refreshAssignmentCompletionStatus(event.processRunId, event.onboardingId);
          }
        } catch (error) {
          console.error('Error refreshing completion status on focus:', error);
        }
      }, 500);
    },

    // Refresh completion status for the currently selected assignment
    async refreshCurrentAssignmentStatus() {
      if (!this.selectedReviewEvent || !this.selectedReviewEvent.processRunId || !this.selectedReviewEvent.onboardingId) {
        return;
      }

      this.refreshingStatus = true;
      try {
        const completionStatus = await this.refreshAssignmentCompletionStatus(
          this.selectedReviewEvent.processRunId, 
          this.selectedReviewEvent.onboardingId
        );
        
        // Update the selected event
        this.selectedReviewEvent.completionStatus = completionStatus;
        
        if (completionStatus) {
          this.$vs.notify({
            title: 'Status Updated',
            text: 'Assignment marked as completed',
            color: 'success'
          });
        } else {
          this.$vs.notify({
            title: 'Status Updated',
            text: 'Assignment is still pending',
            color: 'warning'
          });
        }
      } catch (error) {
        console.error('Error refreshing current assignment status:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to refresh assignment status',
          color: 'danger'
        });
      } finally {
        this.refreshingStatus = false;
      }
    },

    // Refresh completion status for all ProcessRun assignments
    async refreshAllCompletionStatus() {
      this.refreshingAllStatus = true;
      try {
        console.log('Refreshing all completion statuses');
        const events = this.simpleCalendarEvents;
        if (!events || !Array.isArray(events)) {
          console.log('No calendar events available');
          this.$vs.notify({
            title: 'Warning',
            text: 'No calendar events available to refresh',
            color: 'warning'
          });
          return;
        }
        
        const processRunEvents = events.filter(event => 
          event.processRunId && event.onboardingId
        );
        
        console.log(`Found ${processRunEvents.length} ProcessRun events to refresh`);
        
        for (const event of processRunEvents) {
          console.log(`Refreshing: ${event.title}`);
          await this.refreshAssignmentCompletionStatus(event.processRunId, event.onboardingId);
        }
        
        this.$vs.notify({
          title: 'Status Updated',
          text: `Refreshed completion status for ${processRunEvents.length} assignments`,
          color: 'success'
        });
      } catch (error) {
        console.error('Error refreshing all completion status:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to refresh completion status',
          color: 'danger'
        });
      } finally {
        this.refreshingAllStatus = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getModalTitle() {
      if (!this.selectedReviewEvent) return 'Assignment Details';
      return this.selectedReviewEvent.isCompleteSOP ? 'Complete SOP Assignment Details' : 'Review Assignment Details';
    },
    getAssignmentIcon() {
      if (!this.selectedReviewEvent) return '📋';
      return this.selectedReviewEvent.isCompleteSOP ? '📋' : '🔍';
    },
    getAssignmentTypeTitle() {
      if (!this.selectedReviewEvent) return 'Assignment';
      return this.selectedReviewEvent.isCompleteSOP ? 'Complete SOP Assignment' : 'Review Assignment';
    },
    getAssigneeTitle() {
      if (!this.selectedReviewEvent) return 'Assignee';
      return this.selectedReviewEvent.isCompleteSOP ? 'Assignee' : 'Reviewer';
    },

    async checkCompletionStatus(processRunId, onboardingId) {
      try {
        console.log(`Checking completion status for ProcessRun ${processRunId}, Onboarding ${onboardingId}`);
        const query = `
          query ProcessRunStepCompletionByAssignment($processRunId: Int!, $onboardingId: Int!) {
            processRunStepCompletionByAssignment(processRunId: $processRunId, onboardingId: $onboardingId) {
              id
              completedBy
              completedAt
              contextData
            }
          }
        `;

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: query,
          variables: {
            processRunId: parseInt(processRunId),
            onboardingId: parseInt(onboardingId)
          }
        });

        console.log('GraphQL response:', response);

        if (response && response.data && response.data.data && response.data.data.processRunStepCompletionByAssignment) {
          console.log('Found completion status:', response.data.data.processRunStepCompletionByAssignment);
          return response.data.data.processRunStepCompletionByAssignment;
        }
        console.log('No completion status found');
        return null;
      } catch (error) {
        console.error('Error checking completion status:', error);
        return null;
      }
    },

    getStatusClass(event) {
      if (event.completionStatus) {
        return 'completed';
      }
      return event.isOverdue ? 'overdue' : 'pending';
    },

    getStatusText(event) {
      if (event.completionStatus) {
        return 'Completed';
      }
      return event.isOverdue ? 'Overdue' : 'Pending';
    },

    showDeleteConfirmation() {
      this.showDeleteConfirm = true;
    },

    async confirmDeleteAssignment() {
      if (!this.selectedReviewEvent) {
        this.$vs.notify({
          title: 'Error',
          text: 'No assignment selected',
          color: 'danger'
        });
        return;
      }

      this.deletingAssignment = true;
      
      try {
        // Extract the assignment ID from the event ID
        const assignmentId = this.selectedReviewEvent.id.replace('review-', '').replace('complete-sop-', '');
        
        const mutation = `
          mutation DeleteAssignmentById($id: Int!) {
            deleteAssignmentById(id: $id)
          }
        `;

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: mutation,
          variables: {
            id: parseInt(assignmentId)
          }
        });

        if (response && response.data && response.data.data && response.data.data.deleteAssignmentById) {
          this.$vs.notify({
            title: 'Success',
            text: 'Assignment deleted successfully',
            color: 'success'
          });
          
          // Close both modals
          this.showDeleteConfirm = false;
          this.closeReviewDetails();
          
          // Reload the review assignments to update the calendar
          await this.loadReviewAssignments();
        } else {
          throw new Error('Failed to delete assignment');
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to delete assignment',
          color: 'danger'
        });
      } finally {
        this.deletingAssignment = false;
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/vuesax/apps/simple-calendar.scss";

#simple-calendar-app .theme-default .cv-day.today .cv-day-number {
  max-width: 30px;
  max-height: 30px;
}

// Override vue-simple-calendar default styles to show full event titles
#simple-calendar-app .cv-event {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  height: auto !important;
  min-height: 1.65rem;
  line-height: 1.2;
  word-wrap: break-word;
  word-break: break-word;
}

// Modern Assignment Details Styles
.modern-assignment-details {
  padding: 0;
}

.assignment-header {
  margin-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1.5rem;
}

.assignment-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  line-height: 1.4;
}

.assignment-description {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.due-date-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.due-date-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.due-date-icon {
  color: #3b82f6;
  font-size: 1.25rem;
}

.due-date-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.due-date-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.due-date-value {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 600;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.pending {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.status-badge.overdue {
  background-color: #fee2e2;
  color: #dc2626;
}

.assignee-section {
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.assignee-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assignee-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.assignee-email {
  font-size: 1rem;
  color: #1f2937;
  font-weight: 600;
}

.assignee-name {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

// Completion Status Styles
.completion-section {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f0f9ff;
  border-radius: 0.5rem;
  border: 1px solid #0ea5e9;
}

.completion-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.completion-label {
  font-size: 0.875rem;
  color: #0369a1;
  font-weight: 500;
}

.completion-user {
  font-size: 1rem;
  color: #0c4a6e;
  font-weight: 600;
}

.completion-date {
  font-size: 0.875rem;
  color: #0369a1;
}

// Status Badge Styles
.status-badge.completed {
  background-color: #10b981;
  color: white;
}

.status-badge.pending {
  background-color: #f59e0b;
  color: white;
}

.status-badge.overdue {
  background-color: #ef4444;
  color: white;
}

/* TCVelik Calendar Page Styles */
body.tcv-pageSimpleCalendar {
  .vx-navbar-wrapper {
    display: none !important;
  }
  
  .router-content {
    margin-top: 0 !important;
  }
  
  .router-header {
    display: none !important;
  }
}

/* Make calendar taller */
.app-fixed-height {
  height: calc(100vh - 2rem) !important;
  min-height: 800px !important;
}

body.tcv-pageSimpleCalendar .app-fixed-height {
  height: calc(100vh - 1rem) !important;
  min-height: 900px !important;
}
</style>
